import { studyQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import { PaginatedStudiesResponse, Study, StudyPublicGetMany, StudyUpdateResponse, User } from '@/types';
import { QueryClient } from '@tanstack/react-query';

export interface StudyUpdateVariables {
  name?: string;
  description?: string;
}

/**
 * Checks if the current user has permission to edit the study
 * User can edit if they are the owner (is_owner = true)
 */
export const canUserEditStudy = (study: Study | undefined, currentUser: User | null): boolean => {
  if (!study || !currentUser) return false;

  // Allow SuperAdmin to edit any study
  if (currentUser.assumed_roles && Object.values(currentUser.assumed_roles).includes('SuperAdmin')) return true;

  // Check if current user is the owner
  const currentUserAccess = study.allowed_users.find(allowedUser => allowedUser.id === currentUser.id);
  return currentUserAccess?.is_owner ?? false;
};

/**
 * Creates the mutation function for updating a study
 *
 * @param userOrg - The organization ID
 * @param studyId - The study ID
 * @returns A function that takes update variables and returns the updated study
 *
 * CRITICAL: The API endpoint returns a StudyUpdateResponse wrapper object:
 * ```
 * {
 *   study: StudyPublicGetOne,  // The actual study data
 *   message: string             // Success message
 * }
 * ```
 *
 * This function MUST extract and return ONLY the study object (data.study).
 * Returning the full response would pollute the cache with the 'message' field
 * and cause type errors, duplicate data, and incorrect cache merges in EditInPlace.
 *
 * @example
 * // ❌ BAD - Returns wrapper (pollutes cache)
 * return data;
 *
 * // ✅ GOOD - Returns only entity
 * return data.study;
 */
export const createStudyMutationFn = (userOrg: string, studyId: string) => {
  return async (variables: StudyUpdateVariables): Promise<Study> => {
    const data = await apiClient.patch<StudyUpdateResponse>(
      studyQueryKeys.endpoints.detail(userOrg, studyId),
      variables,
    );

    // PATCH endpoint should always return data, but handle null case defensively
    if (data === null) {
      throw new Error('Update endpoint returned no content (unexpected 204 response)');
    }

    // CRITICAL: Extract and return ONLY the study object, not the full wrapper.
    // API returns { study: StudyPublicGetOne, message: string }
    // EditInPlace will merge this into cache: { ...oldData, ...data }
    // If we returned 'data' instead of 'data.study', cache would be polluted with 'message' field
    return data.study;
  };
};

/**
 * Fields that the PATCH /studies/:id endpoint actually updates.
 * The server does NOT recompute aggregate/count fields on a name or description update,
 * and returns `0` (OpenAPI `@default 0`) for those fields in the response.
 * We use an explicit allowlist to prevent zeroing out cached counts.
 *
 * This list is intentionally constrained to keys shared by both StudyPublicGetOne
 * and StudyPublicGetMany so the same merge helper works for both caches.
 */
const PATCH_MUTABLE_FIELDS = [
  'name',
  'description',
  'updated_at',
  'updated_by',
  'updated_by_full_name',
] as const satisfies ReadonlyArray<keyof Study & keyof StudyPublicGetMany>;

/**
 * Safely merges a PATCH response into an existing cached study record, preserving
 * server-computed aggregate fields (num_samples, num_instrument_runs, etc.)
 * that the PATCH endpoint returns as `0` by default.
 *
 * Generic over T so it works for both StudyPublicGetOne (detail cache)
 * and StudyPublicGetMany (list cache).
 */
const mergePatchResponse = <T extends Study | StudyPublicGetMany>(oldData: T, data: Study): T => {
  const patch: Partial<T> = {};
  for (const key of PATCH_MUTABLE_FIELDS) {
    if (data[key] !== undefined) {
      (patch as Record<string, unknown>)[key] = data[key];
    }
  }
  return { ...oldData, ...patch };
};

/**
 * Handles updating all study caches when a study is updated
 *
 * @param userOrg - The organization ID
 * @param studyId - The study ID to update in caches
 * @returns A function that updates all study caches with the updated study data
 *
 * This function receives the unwrapped Study object (not the API response wrapper)
 * because createStudyMutationFn correctly extracts data.study before returning.
 *
 * This function directly updates ALL relevant caches with the returned data from the PATCH response,
 * avoiding unnecessary GET requests:
 * - Individual study detail cache
 * - Studies list cache (used by both card and table views)
 *
 * React Query automatically triggers re-renders in all components using this data,
 * so both the card view and AG Grid table view will update instantly without any
 * additional refresh logic needed.
 *
 * NOTE: Uses mergePatchResponse (allowlist merge) instead of a naïve spread to avoid
 * overwriting server-computed aggregate fields (num_samples, num_instrument_runs, etc.)
 * with the `0` defaults the PATCH endpoint returns for those fields.
 */
export const handleStudyListCacheUpdate = (userOrg: string, studyId: string) => {
  return (queryClient: QueryClient, data: Study, _variables: StudyUpdateVariables) => {
    // 1. Update individual study detail cache
    // This prevents a refetch when viewing the study detail page
    const studyDetailKey = studyQueryKeys.detail(userOrg, studyId);
    queryClient.setQueryData<Study>(studyDetailKey, oldData => {
      if (!oldData) return data;
      return mergePatchResponse(oldData, data);
    });

    // 2. Update studies list cache (used by both card and table views)
    const studiesListKey = studyQueryKeys.listSearch(userOrg);
    queryClient.setQueryData<PaginatedStudiesResponse>(studiesListKey, oldList => {
      if (!oldList?.records) return oldList;

      return {
        ...oldList,
        records: oldList.records.map(study => {
          if (study.id === studyId) {
            return mergePatchResponse(study, data);
          }
          return study;
        }),
      };
    });
  };
};

/**
 * Handles invalidating and refetching studies-related queries after a DELETE operation
 *
 * @param userOrg - The organization ID
 * @returns A function that invalidates all study-related queries and triggers refetches
 *
 * Use this function specifically for DELETE operations where you need to remove a study
 * from all caches and lists. This will cause automatic refetches of all study-related queries.
 *
 * For UPDATE operations, DO NOT use this function. Instead, use handleStudyListCacheUpdate
 * which directly updates caches with the PATCH response data, avoiding unnecessary GET requests.
 *
 * @example
 * ```typescript
 * const deleteMutation = useMutation({
 *   mutationFn: (studyId) => apiClient.delete(studyQueryKeys.endpoints.detail(orgId, studyId)),
 *   onSuccess: () => {
 *     // DELETE has no response data to update cache with, so we must invalidate
 *     handleStudyDeletion(orgId)(queryClient);
 *   },
 * });
 * ```
 */
export const handleStudyDeletion = (userOrg: string) => {
  return (queryClient: QueryClient) => {
    // Invalidate all study-related queries for this organization
    // This will automatically refetch active queries to reflect the deletion
    queryClient.invalidateQueries({
      predicate: query => {
        const key = query.queryKey[0];
        return typeof key === 'string' && key.includes(`orgs/${userOrg}/studies`);
      },
      refetchType: 'active', // Refetch active queries to show updated lists
    });
  };
};
